<template>
  <div class="customcheckerboard-fabric-canvas">
		<canvas
      ref="checkerboardFabricCanvas"
    ></canvas>
  </div>
</template>
<script>
import { fabric } from "@vfdp-customs/customs-fabric";
const TWEEN = require('@tweenjs/tween.js');
const BACKGROUND_COLOR = 'f2f3f4';
fabric.Object.NUM_FRACTION_DIGITS = 9;


export default {
  name: "checkerboardFabricCanvas",
  props: {
    tileConfig: {
      type: Object,
      default: () => {}
    },
    color1: {
      type: String,
      default: 'black'
    },
    color2: {
      type: String,
      default: 'grey'
    },
    animateTiles: {
      type: String,
      default: 'color1'
    },
    animateUsingColor: {
      type: String,
      default: 'red'
    },
    backgroundColor: {
      type: String,
      default: `#${BACKGROUND_COLOR}`
    },
    canvasWidth: {
      type: Number,
      default: 1024
    },
    canvasHeight: {
      type: Number,
      default: 1024
    },
    activeColorSelection: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fabricCanvas: null,
      color1Tiles: [],
      color2Tiles: [],
      columnsGrid: [],
      rowsGrid: [],
      color1OpacityToUse: { alpha: 0.0 },
      color2OpacityToUse: { alpha: 0.0 },
      aminations: {
        color1: null,
        color2: null,
      },
      animationCompleted: false
    };
  },
  watch: {
    color1() {
      const color1 = this.color1 || this.backgroundColor
      this.color1Tiles.forEach(this.setColor(color1))
    },
    color2() {
      const color2 = this.color2 || this.backgroundColor
      this.color2Tiles.forEach(this.setColor(color2))
    },
    animateTiles(newValue) {
      if (newValue) {
        // Make the animation play only when color values are null
        if (!this[newValue]) {
          this.aminations[newValue].start();
        }
      }
    }
  },
  methods: {
    setAnimation(colorOpacityToUse) {
      const showConfig = { alpha: 0.5 };
      const hideConfig = { alpha: 0.0 };
      const tweenShow = new TWEEN.default.Tween(colorOpacityToUse)
        .to(showConfig, 250)
        .delay(10)
        .easing(TWEEN.default.Easing.Quadratic.Out)
        .onUpdate(this.update);
      const tweenHide = new TWEEN.default.Tween(colorOpacityToUse)
        .to(hideConfig, 250)
        .delay(700)
        .easing(TWEEN.default.Easing.Quadratic.In)
        .onUpdate(this.update);
      return tweenShow.chain(tweenHide);
    },
    update() {
      this.color1Tiles.forEach(({ animated }) => {
        animated.set('opacity', this.color1OpacityToUse.alpha);
        this.fabricCanvas.renderAll();
      });
      this.color2Tiles.forEach(({ animated }) => {
        animated.set('opacity', this.color2OpacityToUse.alpha);
        this.fabricCanvas.renderAll();
      });
    },
    animate(time) {
      requestAnimationFrame(this.animate)
      TWEEN.default.update(time)
    },
    /**
     * @function createTile
     * This method creates a tile,
     * Based in the @tileConfig prop values
     */
    createTile() {
      const { width, height, rows, columns } = this.tileConfig;
      const commonRect = {
        width,
        height,
        hasBorders: false,
        hasControls: false,
        selectable: false,
        objectCaching: false,
        strokeWidth: 0
      };

      // Create tile matrix
      [...Array(columns).keys()].forEach(column => {
        const left = column * width;
        let oddTile = column % 2 === 0;

        [...Array(rows).keys()].forEach(row => {
          const top = row * height;
          const tilesColorKey = oddTile ? 'color1Tiles' : 'color2Tiles';
          const colorKey = oddTile ? 'color1' : 'color2';
          const newRect = {
            ...commonRect,
            left,
            top,
            fill: this[colorKey]
          };

          const animatedRect = {
            ...newRect,
            fill: this.animateUsingColor,
            opacity: 0
          };
          let data = {
            square: new fabric.Rect(newRect)
          };
          this.fabricCanvas.add(data.square);
          // This is not needed when the canvas is generated without animation
          if (this.activeColorSelection) {
            const animated = new fabric.Rect(animatedRect);
            animated.on('mousedown', () => this.$emit('input', colorKey));
            data = {
              ...data,
              animated
            };
            this[tilesColorKey].push(data);
            this.fabricCanvas.add(data.animated);
          }
          oddTile = !oddTile
        })
      });
    },
    setColor(color) {
      return ({ square, animated }) => {
        square.set('fill', color);
        this.fabricCanvas.renderAll();
      }
    },
    createGrid() {
      const { width, height, rows, columns } = this.tileConfig;

      // Vertical lines
      [...Array(columns).keys()].forEach(column => {
        const left = column * width;
        this.columnsGrid.push([left, 0, left, height * rows]);
      });
      this.columnsGrid.push([width * columns, 0, width * columns, height * rows]);

      // Horizontal lines
      [...Array(rows).keys()].forEach(row => {
        const top = row * height;
        this.rowsGrid.push([0, top, width * columns, top]);
      });
      this.rowsGrid.push([0, height * rows, width * columns, height * rows]);

      const gridLineSettings = {
        strokeWidth: 0.5,
        stroke: 'grey',
        fill: BACKGROUND_COLOR,
        strokeDashArray: [3],
        hasBorders: false,
        hasControls: false,
        selectable: false
      };
      this.columnsGrid.forEach(vertical => {
        this.fabricCanvas.add(new fabric.Line(vertical, gridLineSettings));
      });
      this.rowsGrid.forEach(horizontal => {
        this.fabricCanvas.add(new fabric.Line(horizontal, gridLineSettings));
      });
    },
    init() {
      const canvas = this.$refs.checkerboardFabricCanvas;
      // calculate width and height based in @tileConfig
      const { width, height, rows, columns } = this.tileConfig;
      let canvasWidth, canvasHeight;
      if (this.activeColorSelection) {
        canvasWidth = width * columns + 1.5;
        canvasHeight = height * rows + 1.5;
      } else {
        canvasWidth = this.$props.canvasWidth;
        canvasHeight = this.$props.canvasHeight;
      }

      this.fabricCanvas = new fabric.Canvas(canvas, {
        backgroundColor: this.backgroundColor,
        selection: false,
        hoverCursor: 'pointer',
        enableRetinaScaling: false
      });
      this.fabricCanvas.setWidth(canvasWidth);
      this.fabricCanvas.setHeight(canvasHeight);

      this.createTile();
      if (this.activeColorSelection) {
        this.createGrid();
        this.aminations.color1 = this.setAnimation(this.color1OpacityToUse);
        this.aminations.color2 = this.setAnimation(this.color2OpacityToUse);
        if (!this.color1) {
          this.aminations.color1.start()
        }
        this.animate();
      }
      this.$emit('canvasLoaded');
    },
  },
  mounted() {
    this.init();
  }
};
</script>
